// Copyright 2017 Sorama B.V.

import { autoinject } from "aurelia-framework";
import {
    HologramSetAnalysisProperties,
    GenericMeasurement as LocalGenericMeasurement,
    MeasurementBeamformingProjectionSetAnalysisProperties,
    MeasurementHologramSetAnalysisProperties,
    MeasurementMetricPsychoAcousticsProperties,
    MeasurementPsychoAcousticsProperties,
    MeasurementSpectrumSetAnalysisProperties,
    MeasurementTimeSignalAnalysisProperties,
} from "datatypes/SoramaLocalApi";
import {
    BeamformingProjectionSet,
    BeamformingProjectionSetAnalysisProperties,
    GenericMeasurement as CloudGenericMeasurement,
    CriticalBandSpectrumSet,
    FrequencySpectrumSet,
    HologramSet,
    MetricPsychoAcousticsProperties,
    PsychoAcousticsAnalysisProperties,
    PsychoAcousticsBandsResults,
    PsychoAcousticsOverTimeResults,
    PsychoAcousticsResults,
    SpectrumSetAnalysisProperties,
    TimeSignal,
} from "datatypes/SoramaWebApi";
import { HttpResponseMessageError } from "errors/HttpResponseMessageError";
import { StringExtensions } from "extensions/StringExtensions";
import { ILocalApi } from "libraries/WebApi/ILocalApi";
import { IWebRequestHandler } from "libraries/WebRequestHandler/IWebRequestHandler";
import { PsychoAcousticsCacheService } from "services/Cache/PsychoAcousticsCacheService";
import { IMeasurementProcessorResolver } from "services/Measurement/IMeasurementProcessorResolver";
import { CloudSoundProcessingService } from "services/SoundProcessing/CloudSoundProcessingService";
import { IBeamformingProcessingService } from "services/SoundProcessing/IBeamformingProcessingService";
import { IHologramProcessingService } from "services/SoundProcessing/IHologramProcessingService";
import { ISpectralProcessingService } from "services/SoundProcessing/ISpectralProcessingService";
import { IPsychoAcousticsProcessingService } from "./IPsychoAcousticsProcessingService";

type GenericMeasurement = CloudGenericMeasurement & LocalGenericMeasurement;

@autoinject
export class LocalSoundProcessingService
    implements
        ISpectralProcessingService,
        IBeamformingProcessingService,
        IHologramProcessingService,
        IPsychoAcousticsProcessingService
{
    /*  These fields are also used by the non-deprecated analysis endpoints.
     **  The last nodes on the endpoint are the same between soundProcessing and analysis at the time of writing */
    private static timeApiCall = "{0}/Time";
    private static spectrumSetApiCall = "{0}/Spectrum/Set";
    private static psychoApiCall = "{0}/Psychoacoustics";
    private static hologramApiCall = "{0}/Hologram";
    private static sensorRMSApiCall = "{0}/Sensor/RMS";
    private static beamformingProjectionApiCall = "{0}/BeamformingProjection";

    private readonly webRequestHandler: IWebRequestHandler;
    private readonly cloudSoundProcessingService: CloudSoundProcessingService;
    private readonly measurementProcessorResolver: IMeasurementProcessorResolver;
    private readonly localApi: ILocalApi;
    private readonly psychoAcousticsCache: PsychoAcousticsCacheService;

    constructor(
        webRequestHandler: IWebRequestHandler,
        cloudSoundProcessingService: CloudSoundProcessingService,
        measurementProcessorResolver: IMeasurementProcessorResolver,
        psychoAcousticsCache: PsychoAcousticsCacheService,
        localApi: ILocalApi
    ) {
        this.webRequestHandler = webRequestHandler;
        this.cloudSoundProcessingService = cloudSoundProcessingService;
        this.measurementProcessorResolver = measurementProcessorResolver;
        this.psychoAcousticsCache = psychoAcousticsCache;
        this.localApi = localApi;
    }

    //#region ISoundProcessingService

    public getTimeSignals(
        measurement: GenericMeasurement,
        properties: MeasurementTimeSignalAnalysisProperties,
        computationProgressCallback?: (progress: number) => void
    ): Promise<TimeSignal[]> {
        return this.localApi.analysis.then((uri) => {
            const apiCall = StringExtensions.format(LocalSoundProcessingService.timeApiCall, uri);
            properties.measurement = measurement;

            let request = this.webRequestHandler
                .createPostRequest<TimeSignal[]>(apiCall, properties)
                .withBSONResponse();

            if (computationProgressCallback && request.supportsProgressWithCurrentSettings()) {
                request = request.withUrl(apiCall + "?withProgress=true").withProgress(computationProgressCallback);
            }

            return this.cloudSoundProcessingService.genericAnalysisCosts(measurement).then(() => {
                return request
                    .send()
                    .then((response) => response.content)
                    .catch((e) => this.onErrorResponse(measurement, e));
            });
        });
    }

    public getSpectrumSet(
        measurement: GenericMeasurement,
        properties: SpectrumSetAnalysisProperties,
        computationProgressCallback?: (progress: number) => void
    ): Promise<FrequencySpectrumSet> {
        return this.localApi.analysis.then((uri) => {
            const apiCall = StringExtensions.format(LocalSoundProcessingService.spectrumSetApiCall, uri);

            const anyProps = <MeasurementSpectrumSetAnalysisProperties>(<unknown>properties);
            // We have to cast here because the generic measurement from the ac is somewhat different?
            anyProps.measurement = <LocalGenericMeasurement>(<unknown>measurement);

            let request = this.webRequestHandler
                .createPostRequest<FrequencySpectrumSet>(apiCall, anyProps)
                .withBSONResponse();

            if (computationProgressCallback && request.supportsProgressWithCurrentSettings()) {
                request = request.withUrl(apiCall + "?withProgress=true").withProgress(computationProgressCallback);
            }

            return this.cloudSoundProcessingService.genericAnalysisCosts(measurement).then(() => {
                return request
                    .send()
                    .then((response) => response.content)
                    .catch((e) => this.onErrorResponse(measurement, e));
            });
        });
    }

    public getPsychoAcoustics(
        measurement: GenericMeasurement,
        properties: PsychoAcousticsAnalysisProperties,
        computationProgressCallback?: (progress: number) => void,
        abortSignal?: AbortSignal
    ): Promise<PsychoAcousticsResults> {
        // Check cache first
        const cachedResults = this.psychoAcousticsCache.getCachedPsychoAcoustics(measurement, properties);
        if (cachedResults) {
            return Promise.resolve(cachedResults);
        }

        return this.localApi.analysis.then((uri) => {
            const apiCall = StringExtensions.format(LocalSoundProcessingService.psychoApiCall, uri);

            const props = <MeasurementPsychoAcousticsProperties>properties;
            props.measurement = <LocalGenericMeasurement>(<unknown>measurement);

            let request = this.webRequestHandler
                .createPostRequest<PsychoAcousticsResults>(apiCall, props)
                .withBSONResponse();

            if (computationProgressCallback && request.supportsProgressWithCurrentSettings()) {
                request = request.withUrl(apiCall + "?withProgress=true");
                request = request.withProgress(computationProgressCallback);
                request = request.withAbortSignal(abortSignal);
            }

            return this.cloudSoundProcessingService.psychoAcousticsAnalysisCosts(measurement).then(() => {
                return request
                    .send()
                    .then((response) => {
                        // Guard: skip if no valid data (with safe navigation)
                        if (!response?.content?.data) return response.content;

                        // Cache the results before returning
                        try {
                            this.psychoAcousticsCache.cachePsychoAcoustics(measurement, properties, response.content);
                        } catch (error) {
                            console.warn("Failed to cache results:", error);
                        }
                        return response.content;
                    })
                    .catch((e) => this.onErrorResponse(measurement, e));
            });
        });
    }

    public getPsychoAcousticMetricOverTime(
        measurement: GenericMeasurement,
        properties: MetricPsychoAcousticsProperties,
        computationProgressCallback?: (progress: number) => void
    ): Promise<PsychoAcousticsOverTimeResults> {
        // Check cache first
        const cachedResults = this.psychoAcousticsCache.getCachedMetricOverTime(measurement, properties);
        if (cachedResults) {
            return Promise.resolve(cachedResults);
        }

        return this.localApi.analysis.then((uri) => {
            const apiCall = StringExtensions.format(LocalSoundProcessingService.psychoApiCall + "/Time", uri);

            const props = <MeasurementMetricPsychoAcousticsProperties>properties;
            props.measurement = <LocalGenericMeasurement>measurement;

            let request = this.webRequestHandler
                .createPostRequest<PsychoAcousticsOverTimeResults>(apiCall, props)
                .withBSONResponse();

            if (computationProgressCallback && request.supportsProgressWithCurrentSettings()) {
                request = request.withUrl(apiCall + "?withProgress=true");
                request = request.withProgress(computationProgressCallback);
            }

            return this.cloudSoundProcessingService.genericAnalysisCosts(measurement).then(() => {
                return request
                    .send()
                    .then((response) => {
                        // Cache the results before returning
                        try {
                            this.psychoAcousticsCache.cacheMetricOverTime(measurement, properties, response.content);
                        } catch (error) {
                            console.warn("Failed to cache results:", error);
                        }
                        return response.content;
                    })
                    .catch((e) => this.onErrorResponse(measurement, e));
            });
        });
    }

    public getPsychoAcousticMetricBands(
        measurement: GenericMeasurement,
        properties: MetricPsychoAcousticsProperties,
        computationProgressCallback?: (progress: number) => void
    ): Promise<PsychoAcousticsBandsResults> {
        return this.localApi.analysis.then((uri) => {
            const apiCall = StringExtensions.format(LocalSoundProcessingService.psychoApiCall + "/CriticalBands", uri);

            const props = <MeasurementMetricPsychoAcousticsProperties>properties;
            props.measurement = <LocalGenericMeasurement>measurement;

            let request = this.webRequestHandler
                .createPostRequest<PsychoAcousticsBandsResults>(apiCall, props)
                .withBSONResponse();

            if (computationProgressCallback && request.supportsProgressWithCurrentSettings()) {
                request = request.withUrl(apiCall + "?withProgress=true");
                request = request.withProgress(computationProgressCallback);
            }

            return this.cloudSoundProcessingService.genericAnalysisCosts(measurement).then(() => {
                return request
                    .send()
                    .then((response) => response.content)
                    .catch((e) => this.onErrorResponse(measurement, e));
            });
        });
    }

    public getPsychoAcousticsCriticalBandSet(
        measurement: GenericMeasurement,
        properties: MetricPsychoAcousticsProperties,
        computationProgressCallback?: (progress: number) => void
    ): Promise<CriticalBandSpectrumSet> {
        return this.localApi.analysis.then((uri) => {
            const apiCall = StringExtensions.format(
                LocalSoundProcessingService.psychoApiCall + "/CriticalBandSet",
                uri
            );

            const props = <MeasurementMetricPsychoAcousticsProperties>properties;
            props.measurement = <LocalGenericMeasurement>measurement;

            let request = this.webRequestHandler
                .createPostRequest<CriticalBandSpectrumSet>(apiCall, props)
                .withBSONResponse();

            if (computationProgressCallback && request.supportsProgressWithCurrentSettings()) {
                request = request.withUrl(apiCall + "?withProgress=true");
                request = request.withProgress(computationProgressCallback);
            }

            return this.cloudSoundProcessingService.genericAnalysisCosts(measurement).then(() => {
                return request
                    .send()
                    .then((response) => response.content)
                    .catch((e) => this.onErrorResponse(measurement, e));
            });
        });
    }

    public getHolograms(
        measurement: GenericMeasurement,
        properties: HologramSetAnalysisProperties,
        computationProgressCallback?: (progress: number) => void
    ): Promise<HologramSet> {
        return this.localApi.analysis.then((uri) => {
            const apiCall = StringExtensions.format(LocalSoundProcessingService.hologramApiCall, uri);

            const props = <MeasurementHologramSetAnalysisProperties>properties;
            // We have to cast here because the generic measurement from the ac is somewhat different?
            props.measurement = <LocalGenericMeasurement>(<unknown>measurement);

            let request = this.webRequestHandler.createPostRequest<HologramSet>(apiCall, props).withBSONResponse();

            if (computationProgressCallback && request.supportsProgressWithCurrentSettings()) {
                request = request.withUrl(apiCall + "?withProgress=true");
                request = request.withProgress(computationProgressCallback);
            }

            return request
                .send()
                .then((response) => response.content)
                .catch((e) => this.onErrorResponse(measurement, e));
        });
    }

    public getSensorRMSValues(measurement: GenericMeasurement): Promise<number[]> {
        return this.localApi.analysis.then((uri) => {
            const apiCall = StringExtensions.format(LocalSoundProcessingService.sensorRMSApiCall, uri);

            return this.webRequestHandler
                .createPostRequest<number[]>(apiCall, measurement)
                .send()
                .then((x) => x.content)
                .catch((e) => this.onErrorResponse(measurement, e));
        });
    }

    public getBeamformingProjections(
        measurement: GenericMeasurement,
        properties: BeamformingProjectionSetAnalysisProperties,
        computationProgressCallback?: (progress: number) => void
    ): Promise<BeamformingProjectionSet> {
        return this.localApi.analysis.then((uri) => {
            const apiCall = StringExtensions.format(LocalSoundProcessingService.beamformingProjectionApiCall, uri);
            const props = <MeasurementBeamformingProjectionSetAnalysisProperties>properties;
            props.measurement = measurement;

            let request = this.webRequestHandler
                .createPostRequest<BeamformingProjectionSet>(apiCall, props)
                .withBSONResponse();

            if (computationProgressCallback && request.supportsProgressWithCurrentSettings()) {
                request = request.withUrl(apiCall + "?withProgress=true").withProgress(computationProgressCallback);
            }

            return this.cloudSoundProcessingService.beamformingAnalysisCosts(measurement).then(() => {
                return request
                    .send()
                    .then((response) => response.content)
                    .catch((e) => this.onErrorResponse(measurement, e));
            });
        });
    }

    private onErrorResponse(measurement: GenericMeasurement, e: HttpResponseMessageError): Promise<never> {
        if (e.name === "AbortError" || e.message?.includes("aborted") || e.statusCode === 0) {
            console.warn("Request was canceled, skipping error handling.");
            return; // silently exit
        }

        if (e.statusCode === 404) {
            return this.measurementProcessorResolver.purgeProcessorStatus(measurement.id).then(() => {
                throw e;
            });
        }

        return Promise.reject(e);
    }

    //#endregion
}
